import { faApple } from '@fortawesome/free-brands-svg-icons';
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormEvent, useEffect, useState } from 'react';
import { Alert, Button, Form, InputGroup, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { useCapacitor } from '../../../hooks/useCapacitor';
import { useMobileDetect } from '../../../hooks/useMobileDetect';
import { createHashedQueryParams } from '../../utils/utils';
import { ErrorModal } from '../ErrorModal/ErrorModal';
import { LoginWithGoogle } from '../LoginWithGoogle/LoginWithGoogle';
import { MFACode } from '../MFACode/MFACode';
import styles from './LoginForm.module.scss';

type LoginFormProps = {
  redirectTo?: string;
  showCancel?: boolean;
  showSignup?: boolean;
  comesFromPublicShop?: boolean;
  selectedProduct?: Object | null;
  navigateAway?: boolean;
  onAuth?: () => any;
  onClose?: () => void;
};

const LoginForm = ({
  redirectTo,
  showCancel = true,
  showSignup = false,
  comesFromPublicShop = false,
  selectedProduct = null,
  navigateAway = true,
  onAuth,
  onClose = () => { },
}: LoginFormProps) => {
  const { t } = useTranslation();
  const { login, checkEmail, recoverPassword, loginWithApple, staticData } = useFirebase();
  const history = useHistory();
  const { isNative, platform } = useCapacitor();
  const { isMobile, isAndroid, isIos } = useMobileDetect();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [appleLoginWarningModalOpen, setAppleLoginWarningModalOpen] = useState(false);
  const [appleLoginProceed, setAppleLoginProceed] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isPasswordRecoveryModalOpen, setIsPasswordRecoveryModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [errorCode, setErrorCode] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isMfaCodeModalOpen, setIsMfaCodeModalOpen] = useState(false);
  const [resolver, setResolver] = useState<any>(null);

  useEffect(() => {
    if (appleLoginWarningModalOpen) setAppleLoginProceed(false);
  }, [appleLoginWarningModalOpen]);

  const completeLogin = () => {
    onClose();
    if (onAuth && typeof onAuth === 'function') onAuth();
    if (comesFromPublicShop) {
      history.replace(
        `/shop?comesFromPublicShop=${comesFromPublicShop}&selectedProductSlug=${(selectedProduct as any).slug}&selectedProductId=${(selectedProduct as any)?.id
        }&selectedProductPriceId=${(selectedProduct as any)?.default_price?.id}`
      );
    } else if (redirectTo) history.replace(redirectTo);
    else if (navigateAway) history.replace('/browse');
  };

  const requestAppleLogin = () => {
    loginWithApple()
      .then(completeLogin)
      .catch((error) => {
        console.error(error);
        if (!error.code) return;
        if (error.code === 'auth/internal-error' && error.message.includes('Unauthorized login location')) setErrorCode('custom/locationError');
        else if (error.code === 'auth/internal-error' && error.message.includes('HTTP Cloud Function')) setErrorCode('custom/password-signup-required');
        else setErrorCode(error.code);
        setIsErrorModalOpen(true);
      });
  };

  useEffect(() => {
    if (!appleLoginWarningModalOpen && appleLoginProceed) requestAppleLogin();
  }, [appleLoginWarningModalOpen, appleLoginProceed]);

  const onLoginSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorCode('');
    try {
      await login(email, password);
      completeLogin();
    } catch (error: any) {
      if (error.code === 'auth/multi-factor-auth-required') {
        setResolver(error.resolver);
        onClose();
        setIsMfaCodeModalOpen(true);
      } else if (error.code === 'auth/wrong-password') {
        checkEmail(email).then((methods) => {
          setErrorCode(methods.includes('google.com') && !methods.includes('password') ? 'custom/google-login-needed' : error.code);
          setIsErrorModalOpen(true);
        });
      } else if (error.code) {
        console.error(error);
        setErrorCode(error.code);
        setIsErrorModalOpen(true);
      }
    }
  };

  const onRecoverySubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    recoverPassword(recoveryEmail)
      .then(() => {
        setIsPasswordRecoveryModalOpen(false);
        setIsSuccessModalOpen(true);
      })
      .catch((error) => {
        setErrorCode(error.code);
        setIsErrorModalOpen(true);
      });
  };

  const isLoginMethodVisible = (method: 'GOOGLE' | 'APPLE') => {
    const isVisible = (target: string) => !!staticData?.SSO?.[method]?.[target];
    return (
      (platform === 'web' && !isMobile && isVisible('web')) ||
      (platform === 'web' && isAndroid && isVisible('android')) ||
      (platform === 'web' && isIos && isVisible('ios')) ||
      (platform === 'android' && isNative && isVisible('android_app')) ||
      (platform === 'ios' && isNative && isVisible('ios_app'))
    );
  };

  const gotoSignup = async () => {
    const selectedSlug = selectedProduct && (selectedProduct as any).slug ? (selectedProduct as any).slug : '';
    const selectedId = selectedSlug ? '' : selectedProduct && (selectedProduct as any).default_price ? (selectedProduct as any).default_price.id : '';
    const search = await createHashedQueryParams({
      category: 'odontoiatra',
      comesFromPublicShop: 'yes',
      selectedProductSlug: selectedSlug,
      selectedProductId: selectedId,
      selectedProductPriceId: (selectedProduct as any)?.default_price?.id || '',
    });
    history.push({ pathname: '/signup', search });
  };

  const hasSso = isLoginMethodVisible('GOOGLE') || isLoginMethodVisible('APPLE');

  return (
    <>
      {staticData.SHOW_SIGNUP_NEEDED_WARNING && isNative && <Alert variant="info">E&apos; {t('loginModal.labels.necessarioRegistrarsi')}</Alert>}
      {hasSso && (
        <div className={styles.providers}>
          {isLoginMethodVisible('GOOGLE') && <LoginWithGoogle className={`${styles.providerButton} ${styles.googleButton}`} onAuthorize={completeLogin} />}
          {isLoginMethodVisible('APPLE') && (
            <button type="button" className={`${styles.providerButton} ${styles.appleButton}`} onClick={() => (staticData.SHOW_APPLE_LOGIN_WARNING ? setAppleLoginWarningModalOpen(true) : requestAppleLogin())}>
              <FontAwesomeIcon icon={faApple} fixedWidth />
              <span>{t('loginModal.labels.accediApple')}</span>
            </button>
          )}
        </div>
      )}
      {hasSso && <div className={styles.divider}>{t('loginModal.labels.oppure')}</div>}
      <Form className={`form-page ${styles.form}`} onSubmit={onLoginSubmit}>
        <Form.Group>
          <Form.Label htmlFor="email">{t('labels.email')}</Form.Label>
          <Form.Control required type="email" id="email" value={email} onChange={(event) => setEmail(event.target.value)} />
        </Form.Group>
        <Form.Group>
          <Form.Label htmlFor="password">{t('labels.password')}</Form.Label>
          <InputGroup>
            <Form.Control required type={passwordVisible ? 'text' : 'password'} id="password" value={password} onChange={(event) => setPassword(event.target.value)} />
            <InputGroup.Append>
              <Button type="button" className={`tootor-button tootor-button--secondary ${styles.passwordToggle}`} onClick={() => setPasswordVisible((visible) => !visible)}>
                <FontAwesomeIcon icon={passwordVisible ? faEyeSlash : faEye} fixedWidth />
              </Button>
            </InputGroup.Append>
          </InputGroup>
          <Form.Text className="mt-2">
            <a href="#password-recovery" onClick={(event) => { event.preventDefault(); setIsPasswordRecoveryModalOpen(true); }}>
              {t('loginModal.labels.dimenticatoPassword')}
            </a>
          </Form.Text>
        </Form.Group>
        {comesFromPublicShop && (
          <div className={styles.signupPrompt}>
            <small>{t('labels.domandaRegistrazione')}</small>
            <Button type="button" className="tootor-button tootor-button--basic" onClick={gotoSignup}>{t('labels.registratiSubito')}</Button>
          </div>
        )}
        <div className={styles.actions}>
          {showCancel && <Button type="button" className="tootor-button tootor-button--basic" onClick={onClose}>{t('labels.annulla')}</Button>}
          {showSignup && <Button type="button" className="tootor-button tootor-button--basic" onClick={() => history.replace('/signup')}>{t('labels.registrati')}</Button>}
          <Button className="tootor-button tootor-button--primary" type="submit">{t(comesFromPublicShop ? 'labels.accediShop' : 'labels.accedi')}</Button>
        </div>
      </Form>

      <ErrorModal isModalOpen={isErrorModalOpen} setIsModalOpen={setIsErrorModalOpen} errorCode={errorCode} />
      <Modal className="tootor-modal" show={isPasswordRecoveryModalOpen} centered={isNative} onHide={() => setIsPasswordRecoveryModalOpen(false)}>
        <Modal.Header><Modal.Title>{t('loginModal.labels.recuperoPassword')}</Modal.Title></Modal.Header>
        <Form className="form-page" onSubmit={onRecoverySubmit}>
          <Modal.Body><Form.Group><Form.Label htmlFor="recoveryEmail">{t('loginModal.labels.inserisciEmail')}</Form.Label><Form.Control required type="email" id="recoveryEmail" value={recoveryEmail} onChange={(event) => setRecoveryEmail(event.target.value)} /></Form.Group></Modal.Body>
          <Modal.Footer><Button className="tootor-button tootor-button--basic" onClick={() => setIsPasswordRecoveryModalOpen(false)}>{t('labels.annulla')}</Button><Button className="tootor-button tootor-button--secondary" type="submit">{t('labels.conferma')}</Button></Modal.Footer>
        </Form>
      </Modal>
      <Modal className="tootor-modal" show={isSuccessModalOpen} centered={isNative} onHide={() => setIsSuccessModalOpen(false)}>
        <Modal.Header><Modal.Title>{t('loginModal.labels.emailInviata')}</Modal.Title></Modal.Header>
        <Modal.Body>{t('loginModal.labels.verificaEmail')}</Modal.Body>
        <Modal.Footer><Button className="tootor-button tootor-button--basic" onClick={() => setIsSuccessModalOpen(false)}>{t('labels.chiudi')}</Button></Modal.Footer>
      </Modal>
      <MFACode show={isMfaCodeModalOpen} onHide={() => setIsMfaCodeModalOpen(false)} resolver={resolver} onSuccess={() => { setIsMfaCodeModalOpen(false); completeLogin(); }} onError={(error) => { setErrorCode(error.code); setIsErrorModalOpen(true); }} />
      <Modal show={appleLoginWarningModalOpen} centered={isNative} onHide={() => setAppleLoginWarningModalOpen(false)}>
        <Modal.Body>{staticData.APPLE_LOGIN_WARNING}</Modal.Body>
        <Modal.Footer><Button className="tootor-button tootor-button--basic" onClick={() => setAppleLoginWarningModalOpen(false)}>{t('labels.annulla')}</Button><Button className="tootor-button tootor-button--secondary" onClick={() => { setAppleLoginWarningModalOpen(false); setAppleLoginProceed(true); }}>{t('labels.prosegui')}</Button></Modal.Footer>
      </Modal>
    </>
  );
};

export { LoginForm };
